import { Fragment, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Card, StatTile, TrendPill } from '../../components/ui/Card';
import { InfoTip } from '../../components/ui/InfoTip';
import { SegmentedControl, type TabItem } from '../../components/ui/Tabs';
import { MeterBar, Nil, Table, TableFooter, TGroupRow, THead, TTotalRow, Td, Th, Tr } from '../../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { useAccess } from '../../hooks/useAccess';
import { getChannelsPage, type ChannelRow, type ChannelsPage } from '../../api/signalOps';
import { formatMoney, formatNumber } from '../../utils/numberFormat';
import { formatMonthYear } from '../../utils/dateFormat';
import { ChannelIcon, CHANNEL_META } from './ChannelIcon';
import { formatEuroCompact } from './format';
import { TREND_CAP_MULTIPLE, trendKind } from './trend';
import { dealValueNote, demoBridgeShort, demoBridgeText, edgeLabel, noTouchException, signedCredit } from './channelsBridge';
import { Alert } from '../../components/ui/Alert';

type Period = '30' | '90' | '180' | '365' | 'mtd' | 'ytd';
type Band = ChannelsPage['bands'][number];

/** The API takes a trailing window of 7–730 days ending now. */
const MIN_DAYS = 7;

/** Below this many days a period-over-period trend is mostly noise ("−100%" on day 3). */
const MIN_TREND_DAYS = 14;

function daysSince(start: Date): number {
  return Math.ceil((Date.now() - start.getTime()) / 86_400_000);
}

function periodDays(period: Period): number {
  const now = new Date();
  if (period === 'mtd') return daysSince(new Date(now.getFullYear(), now.getMonth(), 1));
  if (period === 'ytd') return daysSince(new Date(now.getFullYear(), 0, 1));
  return Number(period);
}

/** MTD and YTD only appear once they span the API's 7-day minimum. */
function periodOptions(): TabItem<Period>[] {
  const opts: TabItem<Period>[] = [
    { id: '30', label: '30 days' },
    { id: '90', label: '90 days' },
    { id: '180', label: '6 months' },
    { id: '365', label: '12 months' },
  ];
  if (periodDays('mtd') >= MIN_DAYS) opts.push({ id: 'mtd', label: 'MTD' });
  if (periodDays('ytd') >= MIN_DAYS) opts.push({ id: 'ytd', label: 'YTD' });
  return opts;
}

/**
 * One money format across the page: compact ("€1.26M", "€698.6k"), the same as
 * the tiles, with the full euros on hover. Nothing becomes the faint dot.
 */
function Money({ value }: { value: number | null | undefined }) {
  if (!value) return <Nil />;
  return <span title={formatMoney(value)}>{formatEuroCompact(value)}</span>;
}

/** One return format everywhere: "0×", "0.4×", "2×". */
function formatReturn(value: number): string {
  return `${Number(value.toFixed(1))}×`;
}

/** Booking credit is a fractional share (a booking split across channels): always one decimal, "3.7", "2.0". */
function formatCredit(value: number): string {
  return value.toFixed(1);
}

function Credit({ value }: { value: number }) {
  if (!value) return <Nil />;
  return <>{formatCredit(value)}</>;
}

/** Booking credit of a row: the API's fractional share, or the touched count on an older build. */
function creditOf(row: ChannelRow): number {
  return row.bookingCredit ?? row.bookings;
}

function Return({ value }: { value: number | null }) {
  if (value === null) return <Nil />;
  // Spend with nothing won is a real result, not missing data: say it plainly.
  return <span className={value >= 1 ? 'text-success-dark' : 'text-text-primary'}>{formatReturn(value)}</span>;
}

function Trend({
  pct,
  current,
  previous,
  measuringSince,
  days,
  tooShort,
}: {
  pct: number | null;
  current: number;
  /** Generated in the previous period; null on an older API build that does not send it. */
  previous: number | null;
  measuringSince: string | null;
  days: number;
  tooShort: boolean;
}) {
  if (tooShort) {
    return (
      <span title={`Fewer than ${MIN_TREND_DAYS} days in this period, so no trend is shown`}>
        <Nil />
      </span>
    );
  }
  const t = trendKind({ pct, current, previous, measuringSince, days });
  if (t.kind === 'none') return <Nil />;
  if (t.kind === 'new') {
    return (
      <span
        className="inline-flex rounded-sm bg-surface-tertiary px-1.5 py-0.5 text-body-xs font-medium text-text-tertiary"
        title={`No data for the previous period: started measuring ${formatMonthYear(t.measuringSince)}`}
      >
        new
      </span>
    );
  }
  if (t.kind === 'capped') {
    return (
      <TrendPill trend="up">
        <span
          title={
            t.pct === null
              ? 'Up from €0 in the previous period'
              : `+${t.pct.toFixed(0)}%${t.prior !== null ? ` on ${formatMoney(t.prior)} the period before` : ''}`
          }
        >
          &gt;{TREND_CAP_MULTIPLE}×
        </span>
      </TrendPill>
    );
  }
  const trend = Math.abs(t.pct) < 1 ? 'neutral' : t.pct > 0 ? 'up' : 'down';
  return (
    <TrendPill trend={trend}>
      {t.pct > 0 ? '+' : t.pct < 0 ? '−' : ''}
      {Math.abs(t.pct).toFixed(0)}%
    </TrendPill>
  );
}

/**
 * Bookings a channel must touch, with a non-zero confidence, before its bar is
 * worth drawing: three 0% bars and one 19% (review #11) read as a verdict on
 * two to five bookings.
 */
const MIN_CONFIDENCE_BOOKINGS = 3;

function hasConfidence(row: ChannelRow): boolean {
  return row.touches > 0 && row.bookings >= MIN_CONFIDENCE_BOOKINGS && row.confidence > 0;
}

/** Hide the cost-tool sub-line when it only repeats the channel name ("LinkedIn Campaign Manager" under "LinkedIn Ads"). */
function distinctTool(tool: string | undefined, label: string): string | null {
  if (!tool) return null;
  const t = tool.toLowerCase();
  const l = label.toLowerCase();
  if (t === l || l.includes(t) || t.includes(l)) return null;
  if (t.split(/\s+/)[0] === l.split(/\s+/)[0]) return null;
  return tool;
}

/** A channel with nothing in the period: no pipeline, no wins, no spend, no touches. */
function isEmptyRow(row: ChannelRow): boolean {
  return !row.generated && !row.won && !row.bookings && !row.touches && !row.cost;
}

/**
 * Return over the channels that carry a cost only. Revenue won by free channels
 * (SEO, referrals…) must not make paid spend look profitable. Null when nothing
 * has a cost; 0 when costed channels won nothing.
 */
function costedReturn(rows: ChannelRow[]): number | null {
  const costed = rows.filter((r) => r.cost !== null && r.cost > 0);
  const cost = costed.reduce((n, r) => n + (r.cost ?? 0), 0);
  if (cost <= 0) return null;
  const won = costed.reduce((n, r) => n + r.won, 0);
  return Math.round((won / cost) * 100) / 100;
}

/** The API's catch-all band holds product usage, CRM lifecycle and feeds; name it after what is shown. */
const PRODUCT_CRM = new Set(['product', 'crm']);
function bandLabel(band: Band, rows: ChannelRow[]): string {
  if (band.band !== 'other') return band.label;
  return rows.every((r) => PRODUCT_CRM.has(r.channel)) ? 'Product & CRM' : 'Product, CRM & feeds';
}

/** Touches and accuracy live in the row tooltip rather than their own columns. */
function rowTooltip(row: ChannelRow, isAdmin: boolean): string {
  const parts = [
    `${formatNumber(row.touches)} touches`,
    `touched ${formatNumber(row.bookings)} booking${row.bookings === 1 ? '' : 's'}`,
  ];
  if (isAdmin) parts.push(row.accuracy === null ? 'accuracy not measurable' : `${row.accuracy.toFixed(1)}% accuracy`);
  if (!row.canCarryUtm) parts.push('cannot carry a UTM, so weighted attribution is its only measure');
  return parts.join(' · ');
}

function howToRead(tooShort: boolean) {
  return (
    <div className="space-y-2">
      <p>
        Generated pipeline is spread across the channels that touched each account, by signal weight. That is the only
        way a channel that never carries a UTM shows up in a number. A booking is worth its linked Attio deal value;
        without one, a flat estimate is used. An account booking again within 30 days counts once.
      </p>
      <p>
        <span className="font-medium">Return</span> is closed-won revenue ÷ cost, shown on channels with a known spend only. Each won deal is assigned to the
        latest booking at or before it closed. The total return counts only channels with a cost, so revenue from free
        channels never props up paid spend. Cost is read for paid channels only; organic, owned and
        product channels have no media spend, so their Cost stays blank. A dot on a paid channel means no cost was read
        from its tool yet: spend unknown, not zero.
      </p>
      <p>
        <span className="font-medium">Trend</span> compares generated pipeline with the previous period of the same length. A channel with no
        data for the previous period (it started measuring after that period began) shows “new”, in every period.
        Growth from €0, or beyond ten times, is shown as “&gt;10×”.
        {tooShort && ` This period has fewer than ${MIN_TREND_DAYS} days, so trends are hidden until it is long enough to compare fairly.`}
      </p>
      <p>
        <span className="font-medium">Booked</span> is booking credit: each booking is split across the channels that
        touched it, the same split as the euros, so the rows add up to the total. Hover a channel for how many bookings it
        touched at all.
      </p>
      <p>
        <span className="font-medium">Confidence</span> (admin): how far to trust the channel’s euros. The share of its
        bookings valued from a real Attio deal, times the share of its company links that were deterministic. A
        channel whose bookings all use the flat estimate would read 0%, and a channel with fewer than three bookings says too little: both are left blank. Hover a channel for its touches and, for admins, its
        accuracy (share of bookings a second source corroborates).
      </p>
    </div>
  );
}

const UNATTRIBUTED_NOTE =
  'Bookings no channel has a claim on. “No touches”: the account was matched, but no signal from any channel touched it before it booked. “No company match”: demos on Sales › Demo bookings that attribution could not place on a company we track, so they carry no euros.';

/** "€25k assumed per booking…" — the estimate stated wherever Generated is shown. */
function estimateNote(v: ChannelsPage['valuation']): string {
  if (!v) return 'A flat estimate per booking; real deal values are not available from this API build.';
  const each = formatEuroCompact(v.defaultBookingValue);
  return `${each} assumed per booking without a deal value; ${formatNumber(v.withDealValue)} of ${formatNumber(
    v.bookings,
  )} bookings use real Attio deal values. An account booking again within ${v.repeatWindowDays} days counts once.`;
}

/** What the Bookings tile counts: the Sales › Demo bookings rule, and what it leaves out. */
function bookingsNote(v: ChannelsPage['valuation']): string {
  const rule =
    'Calendly bookings of a demo event type, the same rule as Sales › Demo bookings. Discovery calls, partner slots, pricing consults and other meetings are not bookings.';
  if (v?.nonDemoExcluded === undefined) return rule;
  return `${rule} ${formatNumber(v.nonDemoExcluded)} such meeting${v.nonDemoExcluded === 1 ? '' : 's'} in this period left out.`;
}

/** A bridge row: a label and a signed count in the Bookings column, nothing else. */
function BridgeRow({
  label,
  detail,
  value,
  cols,
}: {
  label: string;
  detail: string;
  value: string;
  cols: { anyWon: boolean; showConfidence: boolean; showTrend: boolean };
}) {
  return (
    <Tr>
      <Td>
        <div className="flex items-center gap-2.5">
          <ChannelIcon channel="unattributed" label="±" />
          <div className="min-w-0">
            <p className="text-text-primary">{label}</p>
            <p className="text-body-xs text-text-tertiary">{detail}</p>
          </div>
        </div>
      </Td>
      <Td align="right">
        <Nil />
      </Td>
      <Td align="right">
        <Nil />
      </Td>
      {cols.anyWon && (
        <Td align="right">
          <Nil />
        </Td>
      )}
      {cols.anyWon && (
        <Td align="right">
          <Nil />
        </Td>
      )}
      <Td align="right" className="tabular-nums text-text-secondary">
        {value}
      </Td>
      {cols.showConfidence && (
        <Td align="right">
          <Nil />
        </Td>
      )}
      {cols.showTrend && (
        <Td align="right">
          <Nil />
        </Td>
      )}
    </Tr>
  );
}

export function ChannelsView() {
  const { isAdmin } = useAccess();
  const [period, setPeriod] = useState<Period>('90');
  const [showAll, setShowAll] = useState(false);
  const options = periodOptions();
  const requestedDays = periodDays(period);
  // The previous period's page stays on screen while the next one loads, so the
  // tiles and the table don't collapse to a spinner on every period switch.
  const { data, isLoading, isError, error, refetch, isPlaceholderData } = useQuery({
    queryKey: ['signal-ops', 'channels', requestedDays],
    queryFn: () => getChannelsPage(requestedDays),
    placeholderData: keepPreviousData,
  });
  // Labels follow the numbers on screen, not the toggle: while the new period
  // loads, the old numbers keep the old period's label (review #9 saw "Last 30
  // days" over 90-day numbers for 2–5 s).
  const [shownPeriod, setShownPeriod] = useState<Period>(period);
  if (data && !isPlaceholderData && shownPeriod !== period) setShownPeriod(period);
  const days = periodDays(shownPeriod);
  const tooShort = days < MIN_TREND_DAYS;
  const periodLabel = options.find((o) => o.id === shownPeriod)?.label ?? `${days} days`;
  const periodText =
    shownPeriod === 'mtd' || shownPeriod === 'ytd' ? `${periodLabel} (${days} days)` : `Last ${periodLabel}`;

  const header = (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <InfoTip>{howToRead(tooShort)}</InfoTip>
      <SegmentedControl options={options} value={period} onChange={setPeriod} ariaLabel="Period" />
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {header}
        <LoadingState rows={2} height={112} />
        <LoadingState rows={6} height={480} />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="space-y-4">
        {header}
        <ErrorState title="Couldn’t load the channel page" error={error} onRetry={() => refetch()} />
      </div>
    );
  }
  if (!data) return null;

  const allRows = data.bands.flatMap((b) => b.rows);
  const hiddenCount = allRows.filter(isEmptyRow).length;
  const bands = data.bands
    .map((b) => {
      const visible = showAll ? b.rows : b.rows.filter((r) => !isEmptyRow(r));
      // Empty channels sink to the bottom of their group; otherwise keep the API order.
      const rows = [...visible].sort((a, z) => Number(isEmptyRow(a)) - Number(isEmptyRow(z)));
      return { ...b, rows, label: bandLabel(b, rows) };
    })
    .filter((b) => b.rows.length > 0)
    // A group of one shows no header (it would repeat its channel), so it goes above the
    // headed groups: placed after one, its row would read as a member of that group.
    .sort((a, z) => Number(a.rows.length > 1) - Number(z.rows.length > 1) || z.generated - a.generated);
  const visibleRows = bands.reduce((n, b) => n + b.rows.length, 0);
  const demos = data.demos;
  // The no-company residual, read from the bridge itself so the row and its
  // tooltip state one number (sales − matched also held the cancelled demos and
  // the edge cases, which read 20 against the tooltip's 21).
  const noMatch = demos?.noCompany ?? 0;
  // Confidence says nothing when every row reads 0%: the column goes, the footer says why.
  // One row with a value among eleven dashes is noise too: it needs at least two.
  const confidenceRows = allRows.filter(hasConfidence).length;
  const anyConfidence = confidenceRows >= 2;
  const showConfidence = isAdmin && anyConfidence;
  // Trend says nothing when no row has one (a month-to-date period, or nothing
  // in the period before): the column goes, the footer says why.
  const showTrend =
    !tooShort &&
    allRows.some(
      (r) =>
        trendKind({
          pct: r.trendPct,
          current: r.generated,
          previous: r.priorGenerated ?? null,
          measuringSince: r.measuringSince,
          days,
        }).kind !== 'none',
    );
  // Won and Return say nothing while nothing has been won: both columns go, and the footer says so.
  const anyWon = data.totals.won > 0 || allRows.some((r) => r.won > 0);
  const colWidths = [
    'auto',
    '8.5rem',
    '8rem',
    ...(anyWon ? ['8rem', '6.5rem'] : []),
    '5.5rem',
    ...(showConfidence ? ['8.5rem'] : []),
    ...(showTrend ? ['6.5rem'] : []),
  ];
  const colCount = colWidths.length;
  const sinceYears = new Set(allRows.filter((r) => r.measuringSince).map((r) => new Date(r.measuringSince as string).getFullYear()));
  const sinceSpread = sinceYears.size > 1;
  const totalReturn = costedReturn(allRows);
  const un = data.unattributed;
  // + Unattributed, always shown; + the bridge when it has a row.
  const groupCount = bands.length + 1 + (demos && (demos.cancelled !== 0 || demos.other !== 0) ? 1 : 0);
  const valuation = data.valuation;
  const allReal = !!valuation && valuation.bookings > 0 && valuation.withDealValue === valuation.bookings;
  const generatedLabel = allReal ? 'Generated' : 'Generated (est.)';
  // The tile headlines what channels can claim; the table's Total row is the whole.
  const note = estimateNote(valuation);
  // Deal-valued bookings a channel touched; 0 means every deal-valued one sits on No touches.
  const channelDealValued = allRows.reduce((n, r) => n + (r.dealValued ?? 0), 0);
  const noTouch = noTouchException(un.generated, data.totals.generated);
  // Headline the euros a channel can claim; the no-touch part stays visible beside it, not inside it.
  const attributedGenerated = noTouch ? Math.max(0, data.totals.generated - un.generated) : data.totals.generated;
  const splitNote = noTouch
    ? `of ${formatEuroCompact(data.totals.generated)} total · ${formatEuroCompact(un.generated)} no touches`
    : '';

  return (
    <div className={clsx('space-y-4 transition-opacity', isPlaceholderData && 'opacity-60')} aria-busy={isPlaceholderData}>
      {header}

      <div className={clsx('grid grid-cols-2 gap-4', anyWon ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}>
        <StatTile
          label="Demo bookings"
          value={formatNumber(data.totals.bookings)}
          sub={
            demos
              ? `${periodText} · ${demoBridgeShort(demos)}`
              : `${periodText} · demo event types only, repeats within 30 days merged`
          }
          tooltip={demos ? `${demoBridgeText(demos)} ${bookingsNote(valuation)}` : bookingsNote(valuation)}
        />
        <StatTile
          label={noTouch ? `${generatedLabel}, attributed` : generatedLabel}
          value={formatEuroCompact(attributedGenerated) || '€0'}
          sub={
            valuation
              ? `${periodText} · ${splitNote}${splitNote ? ' · ' : ''}${dealValueNote(valuation.withDealValue, valuation.bookings, channelDealValued)}`
              : `${periodText} · flat estimate per booking`
          }
          tooltip={note}
        />
        {/* Nothing closed-won: no Won tile showing €0 as if it were data; the spend tile says return is not measurable. */}
        {anyWon && (
          <StatTile
            label="Won"
            value={formatEuroCompact(data.totals.won) || '€0'}
            sub={`${periodText} · closed-won in Attio, by booking date`}
          />
        )}
        <StatTile
          label={anyWon ? 'Paid spend → return' : 'Paid spend'}
          value={formatEuroCompact(data.totals.cost) || '€0'}
          sub={
            !anyWon
              ? `${periodText} · return not measurable: nothing closed-won from these bookings yet`
              : totalReturn === null
                ? `${periodText} · no spend read`
                : `${periodText} · ${formatReturn(totalReturn)} won on paid channels`
          }
        />
      </div>

      {noTouch && <Alert variant="warning">{noTouch.text}</Alert>}

      <Card padding="sm">
        {data.totals.bookings === 0 && bands.length === 0 ? (
          <EmptyState
            title="No attributed bookings in this period"
            description="Attribution needs the signal ledger filled and at least one demo booking resolved to an account."
            compact
          />
        ) : (
          <>
            <Table minWidth={showConfidence ? 940 : 820} colWidths={colWidths}>
              <THead>
                <tr>
                  <Th>Channel</Th>
                  <Th align="right">
                    {allReal ? (
                      generatedLabel
                    ) : (
                      <span className="inline-flex items-center justify-end gap-1">
                        {generatedLabel}
                        <InfoTip label="How Generated is valued" className="normal-case font-normal">
                          {note}
                        </InfoTip>
                      </span>
                    )}
                  </Th>
                  <Th align="right">Cost</Th>
                  {anyWon && <Th align="right">Won</Th>}
                  {anyWon && <Th align="right">Return</Th>}
                  <Th align="right">
                    <span className="inline-flex items-center justify-end gap-1">
                      Booked
                      <InfoTip label="How Booked is counted" className="normal-case font-normal">
                        Booking credit, not a head count: each booking is split across the channels that touched it, the
                        same split as the euros, so the rows add up to the total and a share shows as 3.7 or 0.2 (one
                        decimal everywhere). A channel touching three bookings with a third of each shows 1.0. Hover a
                        channel for how many whole bookings it touched.
                      </InfoTip>
                    </span>
                  </Th>
                  {showConfidence && <Th align="right">Confidence</Th>}
                  {showTrend && <Th align="right">Trend</Th>}
                </tr>
              </THead>
              <tbody>
                {bands.map((band) => {
                  const hasCost = band.rows.some((r) => r.cost !== null);
                  const summary = [
                    `${formatEuroCompact(band.generated) || '€0'} generated`,
                    hasCost ? `${formatEuroCompact(band.cost) || '€0'} cost` : null,
                    anyWon ? `${formatEuroCompact(band.won) || '€0'} won` : null,
                    `${formatCredit(Math.round(band.rows.reduce((n, r) => n + creditOf(r), 0) * 10) / 10)} booked`,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <Fragment key={band.band}>
                      {/* A group of one repeats its only channel ("Events" over "Webinars"): no header. */}
                      {band.rows.length > 1 && <TGroupRow label={band.label} summary={summary} colSpan={colCount} />}
                      {band.rows.map((row) => {
                        const meta = CHANNEL_META[row.channel];
                        // The tool line only helps when it says something the channel name does not.
                        const tool = distinctTool(meta?.tool, row.label);
                        const empty = isEmptyRow(row);
                        // Spend with nothing generated: one note under the name, not a dot in every column.
                        const costOnly = !!row.cost && !row.generated && !row.won && !creditOf(row);
                        return (
                          <Tr key={row.channel}>
                            <Td>
                              <div className="flex items-center gap-2.5" title={rowTooltip(row, isAdmin)}>
                                <ChannelIcon channel={row.channel} label={row.label} />
                                <div className="min-w-0">
                                  <p className="truncate text-text-primary">{row.label}</p>
                                  {costOnly ? (
                                    <p className="truncate text-body-xs text-text-tertiary">
                                      No bookings touched this channel in this period
                                    </p>
                                  ) : (tool || row.measuringSince) && (
                                    <p className="truncate text-body-xs text-text-tertiary">
                                      {[tool, row.measuringSince ? `since ${formatMonthYear(row.measuringSince)}` : null]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </Td>
                            <Td align="right" className="tabular-nums text-text-primary">
                              {costOnly ? null : <Money value={row.generated} />}
                            </Td>
                            <Td align="right" className="tabular-nums text-text-secondary">
                              {row.cost === null && band.band !== 'paid' ? null : row.cost === null ? (
                                <span
                                  title={
                                    tool
                                      ? `No cost read from ${tool} yet, so spend is unknown`
                                      : 'No cost source wired for this channel'
                                  }
                                >
                                  <Nil />
                                </span>
                              ) : (
                                <span title={formatMoney(row.cost)}>{formatEuroCompact(row.cost) || "€0"}</span>
                              )}
                            </Td>
                            {anyWon && (
                              <Td align="right" className="tabular-nums text-text-secondary">
                                {costOnly ? null : <Money value={row.won} />}
                              </Td>
                            )}
                            {anyWon && (
                              <Td align="right" className="tabular-nums">
                                {/* Return only means something where we know the spend: paid rows. */}
                                {costOnly ? null : row.cost === null ? <Nil /> : <Return value={row.moneyReturn} />}
                              </Td>
                            )}
                            <Td
                              align="right"
                              className="tabular-nums text-text-secondary"
                              title={`Touched ${formatNumber(row.bookings)} booking${row.bookings === 1 ? '' : 's'}`}
                            >
                              {costOnly ? null : <Credit value={creditOf(row)} />}
                            </Td>
                            {showConfidence && (
                              <Td align="right">
                                {costOnly ? null : empty || !hasConfidence(row) ? (
                                  <span
                                    title={
                                      row.touches === 0
                                        ? undefined
                                        : row.bookings < MIN_CONFIDENCE_BOOKINGS
                                          ? `Only ${row.bookings} booking${row.bookings === 1 ? '' : 's'} touched: too few to judge`
                                          : 'No booking at a real deal value and no deterministic identity yet'
                                    }
                                  >
                                    <Nil />
                                  </span>
                                ) : (
                                  <span
                                    className="inline-flex items-center justify-end gap-2"
                                    title={[
                                      row.dealValued !== undefined
                                        ? `${formatNumber(row.dealValued)} of ${formatNumber(row.bookings)} bookings at real deal value`
                                        : null,
                                      row.identityConfidence !== undefined
                                        ? `identity ${row.identityConfidence.toFixed(0)}% deterministic`
                                        : null,
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  >
                                    <MeterBar value={row.confidence / 100} />
                                    <span className="tabular-nums text-body-xs text-text-tertiary">
                                      {row.confidence.toFixed(0)}%
                                    </span>
                                  </span>
                                )}
                              </Td>
                            )}
                            {showTrend && (
                              <Td align="right">
                              {costOnly ? null : (
                                <Trend
                                  pct={row.trendPct}
                                  current={row.generated}
                                  previous={row.priorGenerated ?? null}
                                  measuringSince={row.measuringSince}
                                  days={days}
                                  tooShort={tooShort}
                                />
                              )}
                            </Td>
                            )}
                          </Tr>
                        );
                      })}
                    </Fragment>
                  );
                })}

                <TGroupRow
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      Unattributed
                      <InfoTip label="What unattributed means">{UNATTRIBUTED_NOTE}</InfoTip>
                    </span>
                  }
                  summary={[
                    `${formatEuroCompact(un.generated) || '€0'} generated`,
                    anyWon ? `${formatEuroCompact(un.won) || '€0'} won` : null,
                    `${formatCredit(Math.round((un.bookings + noMatch) * 10) / 10)} booked`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  colSpan={colCount}
                />
                <Tr>
                  <Td>
                    <div className="flex items-center gap-2.5">
                      <ChannelIcon channel="unattributed" label="?" />
                      <div className="min-w-0">
                        <p className="truncate text-text-primary">No touches</p>
                        <p className="truncate text-body-xs text-text-tertiary">Matched to a company, no channel before the booking</p>
                      </div>
                    </div>
                  </Td>
                  <Td align="right" className="tabular-nums text-text-primary">
                    <Money value={un.generated} />
                  </Td>
                  <Td align="right">
                    <Nil />
                  </Td>
                  {anyWon && (
                    <Td align="right" className="tabular-nums text-text-secondary">
                      <Money value={un.won} />
                    </Td>
                  )}
                  {anyWon && (
                    <Td align="right">
                      <Nil />
                    </Td>
                  )}
                  <Td
                    align="right"
                    className="tabular-nums text-text-secondary"
                    title="Booking credit: matched bookings no channel touched before they booked"
                  >
                    <Credit value={un.bookings} />
                  </Td>
                  {showConfidence && (
                    <Td align="right">
                      <Nil />
                    </Td>
                  )}
                  {showTrend && (
                    <Td align="right">
                      <Nil />
                    </Td>
                  )}
                </Tr>
                {demos && noMatch > 0 && (
                  <Tr>
                    <Td>
                      <div className="flex items-center gap-2.5" title={demoBridgeText(demos)}>
                        <ChannelIcon channel="unattributed" label="?" />
                        <div className="min-w-0">
                          <p className="truncate text-text-primary">No company match</p>
                          <p className="truncate text-body-xs text-text-tertiary">
                            On Sales › Demo bookings, not placed on a company · not valued
                          </p>
                        </div>
                      </div>
                    </Td>
                    <Td align="right" title="Not valued: a booking needs a company to carry a deal value">
                      <Nil />
                    </Td>
                    <Td align="right">
                      <Nil />
                    </Td>
                    {anyWon && (
                      <Td align="right">
                        <Nil />
                      </Td>
                    )}
                    {anyWon && (
                      <Td align="right">
                        <Nil />
                      </Td>
                    )}
                    <Td align="right" className="tabular-nums text-text-secondary" title={demoBridgeText(demos)}>
                      <Credit value={noMatch} />
                    </Td>
                    {showConfidence && (
                      <Td align="right">
                        <Nil />
                      </Td>
                    )}
                    {showTrend && (
                    <Td align="right">
                      <Nil />
                    </Td>
                  )}
                  </Tr>
                )}
                {demos && (demos.cancelled !== 0 || demos.other !== 0) && (
                  <>
                    <TGroupRow
                      label={
                        <span className="inline-flex items-center gap-1.5">
                          Bridge to Sales › Demo bookings
                          <InfoTip label="What the bridge rows are">
                            Demos Sales counts that attribution does not, or the other way round. With them the Bookings
                            column adds up to the Total. {demoBridgeText(demos)}
                          </InfoTip>
                        </span>
                      }
                      summary={`${signedCredit(demos.cancelled + demos.other)} booked`}
                      colSpan={colCount}
                    />
                    {demos.cancelled !== 0 && (
                      <BridgeRow
                        label="Cancelled"
                        detail="On Sales › Demo bookings; attribution reads live bookings only"
                        value={demos.cancelled.toFixed(1)}
                        cols={{ anyWon, showConfidence, showTrend }}
                      />
                    )}
                    {demos.other !== 0 && (
                      <BridgeRow
                        label={edgeLabel(demos.other)}
                        detail={
                          demos.other < 0
                            ? 'Attribution dates these inside the period, Sales just outside it'
                            : 'Counted by Sales, merged or dated outside the period by attribution'
                        }
                        value={signedCredit(demos.other)}
                        cols={{ anyWon, showConfidence, showTrend }}
                      />
                    )}
                  </>
                )}
              </tbody>
              <tfoot>
                <TTotalRow>
                  <Td>Total</Td>
                  <Td align="right" className="tabular-nums"><Money value={data.totals.generated} /></Td>
                  <Td align="right" className="tabular-nums"><Money value={data.totals.cost} /></Td>
                  {anyWon && <Td align="right" className="tabular-nums"><Money value={data.totals.won} /></Td>}
                  {anyWon && (
                    <Td
                      align="right"
                      className="tabular-nums"
                      title="Won by channels with a cost ÷ their cost"
                    >
                      {totalReturn === null ? (
                        <Nil />
                      ) : (
                        // "on paid": the total counts costed channels only, so a big Won beside it is not a contradiction.
                        <span className={totalReturn >= 1 ? 'text-success-dark' : 'text-text-primary'}>
                          {formatReturn(totalReturn)} <span className="text-body-xs text-text-tertiary">on paid</span>
                        </span>
                      )}
                    </Td>
                  )}
                  <Td
                    align="right"
                    className="tabular-nums"
                    title={
                      demos
                        ? demoBridgeText(demos)
                        : 'Distinct bookings. A booking touched by several channels is counted on each channel row, once here.'
                    }
                  >
                    <Credit value={data.totals.bookings} />
                  </Td>
                  {showConfidence && <Td />}
                  {showTrend && <Td />}
                </TTotalRow>
              </tfoot>
            </Table>
            <TableFooter>
              <span className="flex flex-wrap items-center gap-x-1">
                <span>
                  {visibleRows} channels · {groupCount} groups · {formatNumber(data.totals.bookings)} bookings ·{' '}
                  {shownPeriod === 'mtd' || shownPeriod === 'ytd' ? periodText : periodText.toLowerCase()}
                  {' · Booked is booking credit: a booking several channels touched is split between them (shares like 3.7 or 0.2), the total counts it once'}
                  {!allReal && ` · ${generatedLabel.toLowerCase()} uses an estimate where no deal value is linked`}
                  {sinceSpread && ' · “since” is the first signal we hold for a channel: website activity includes imported older history, the other channels start when they were connected'}
                  {!anyWon && ' · Won and Return hidden: nothing closed-won in this period'}
                  {!showTrend &&
                    (tooShort
                      ? ` · Trend hidden: fewer than ${MIN_TREND_DAYS} days in this period`
                      : ' · Trend hidden: no channel has a period before this one to compare with')}
                  {isAdmin &&
                    !anyConfidence &&
                    ` · Confidence hidden: ${
                      confidenceRows === 1
                        ? 'only one channel has enough bookings valued from a real deal to score'
                        : '0% on every channel, because no booking a channel touched is valued from a real deal'
                    }${
                      valuation && valuation.withDealValue > 0 && channelDealValued === 0
                        ? ` (the ${formatNumber(valuation.withDealValue)} that are sit on No touches)`
                        : ''
                    }`}
                </span>
                {hiddenCount > 0 && (
                  <>
                    <span>·</span>
                    <button
                      type="button"
                      onClick={() => setShowAll((v) => !v)}
                      className="underline-offset-2 hover:text-text-secondary hover:underline"
                    >
                      {showAll ? 'Hide empty channels' : `Show ${hiddenCount} empty channels`}
                    </button>
                  </>
                )}
              </span>
            </TableFooter>
          </>
        )}
      </Card>
    </div>
  );
}
